import React from "react";
import { Box, Typography, Paper, Chip, alpha, useTheme } from "@mui/material";
import { ShoppingCart, Restaurant } from "@mui/icons-material";
import ImageLoader from "../../../../components/ImageLoader/ImageLoader";

const OfferItemsSection = ({ offerDetails }) => {
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        bgcolor: "white",
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        borderRadius: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <ShoppingCart
          sx={{ color: theme.palette.primary.main, fontSize: 20 }}
        />
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 700,
            fontSize: "0.95rem",
            color: theme.palette.text.primary,
          }}
        >
          Included Items ({offerDetails.OfferItems?.length || 0})
        </Typography>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        {offerDetails.OfferItems && offerDetails.OfferItems.length > 0 ? (
          offerDetails.OfferItems.map((offerItem, index) => {
            const menuItem = offerItem.MenuItem;
            if (!menuItem) return null;

            return (
              <Box
                key={offerItem.id}
                sx={{
                  display: "flex",
                  gap: 1.5,
                  p: 1.5,
                  bgcolor: alpha(theme.palette.primary.main, 0.02),
                  borderRadius: 1.5,
                  border: `1px solid ${alpha(theme.palette.divider, 0.05)}`,
                }}
              >
                {/* Item Image */}
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: 1.5,
                    overflow: "hidden",
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {menuItem.image ? (
                    <ImageLoader
                      imagePath={menuItem.image}
                      width={80}
                      height={80}
                      alt={menuItem.name}
                      borderRadius={1.5}
                    />
                  ) : (
                    <Restaurant
                      sx={{
                        fontSize: 32,
                        color: alpha(theme.palette.primary.main, 0.3),
                      }}
                    />
                  )}
                </Box>

                {/* Item Details */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.9rem",
                        color: theme.palette.text.primary,
                      }}
                    >
                      {menuItem.name}
                    </Typography>
                    <Chip
                      label={`x${offerItem.quantity}`}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                      }}
                    />
                  </Box>

                  {/* Description */}
                  {menuItem.description && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: "0.8rem",
                        mb: 1,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {menuItem.description}
                    </Typography>
                  )}

                  {/* Badges */}
                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                    {menuItem.isVegetarian && (
                      <Chip
                        label="Veg"
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.65rem",
                          bgcolor: alpha(theme.palette.success.main, 0.1),
                          color: theme.palette.success.main,
                        }}
                      />
                    )}
                    {menuItem.isVegan && (
                      <Chip
                        label="Vegan"
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.65rem",
                          bgcolor: alpha(theme.palette.success.main, 0.1),
                          color: theme.palette.success.main,
                        }}
                      />
                    )}
                    {menuItem.isSpicy && (
                      <Chip
                        label="Spicy"
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: "0.65rem",
                          bgcolor: alpha(theme.palette.error.main, 0.1),
                          color: theme.palette.error.main,
                        }}
                      />
                    )}
                    {menuItem.hasVariations &&
                      menuItem.ItemVariations?.length > 0 && (
                        <Chip
                          label={`${menuItem.ItemVariations.length} Variations`}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: "0.65rem",
                            bgcolor: alpha(theme.palette.info.main, 0.1),
                            color: theme.palette.info.main,
                          }}
                        />
                      )}
                  </Box>

                  {/* Base Price */}
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: "0.75rem",
                      mt: 0.5,
                      display: "block",
                    }}
                  >
                    Base Price: Rs. {parseFloat(menuItem.basePrice).toFixed(2)}
                  </Typography>

                  {/* Preparation Time */}
                  {menuItem.preparationTime && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: "0.75rem",
                        display: "block",
                      }}
                    >
                      Prep Time: {menuItem.preparationTime} mins
                    </Typography>
                  )}

                  {/* Variations Details */}
                  {menuItem.hasVariations &&
                    menuItem.ItemVariations?.length > 0 && (
                      <Box
                        sx={{
                          mt: 1,
                          pt: 1,
                          borderTop: `1px solid ${alpha(
                            theme.palette.divider,
                            0.08,
                          )}`,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.text.secondary,
                            fontSize: "0.7rem",
                            textTransform: "uppercase",
                            fontWeight: 600,
                            display: "block",
                            mb: 0.5,
                          }}
                        >
                          Available Variations
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.5,
                          }}
                        >
                          {menuItem.ItemVariations.filter(
                            (v) => v.isActive,
                          ).map((variation) => (
                            <Box
                              key={variation.id}
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                p: 0.5,
                                bgcolor: alpha(
                                  theme.palette.primary.main,
                                  0.04,
                                ),
                                borderRadius: 0.5,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{ fontSize: "0.75rem" }}
                              >
                                {variation.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: "0.75rem",
                                  fontWeight: 600,
                                  color: theme.palette.primary.main,
                                }}
                              >
                                Rs. {parseFloat(variation.price).toFixed(2)}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                </Box>
              </Box>
            );
          })
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", py: 2 }}
          >
            No items included in this offer
          </Typography>
        )}
      </Box>
    </Paper>
  );
};

export default OfferItemsSection;
